import { queryOptions } from '@tanstack/react-query';
import type { ModerationListFilter } from '../model/list-params';
import type { ModerationEntityType } from '../lib/moderation-config';
import type { ChangeRequest } from '../model/schema';
import { moderationApi } from './moderation-api';

/**
 * Query-key factory + query-options for the moderation queues. Mutations
 * invalidate through `moderationKeys` (never an inline array).
 */
export const moderationKeys = {
  all: ['moderations'] as const,
  lists: () => [...moderationKeys.all, 'list'] as const,
  list: (entityType: ModerationEntityType, filter: ModerationListFilter) =>
    [...moderationKeys.lists(), entityType, filter] as const,
  cards: () => [...moderationKeys.all, 'card'] as const,
  card: (entityType: ModerationEntityType, entityId: string) =>
    [...moderationKeys.cards(), entityType, entityId] as const,
  requests: () => [...moderationKeys.all, 'request'] as const,
  request: (changeRequestId: string) =>
    [...moderationKeys.requests(), changeRequestId] as const,
};

export const moderationQueries = {
  list: (entityType: ModerationEntityType, filter: ModerationListFilter) =>
    queryOptions({
      queryKey: moderationKeys.list(entityType, filter),
      queryFn: () => moderationApi.list(entityType, filter),
    }),

  card: (entityType: ModerationEntityType, entityId: string) =>
    queryOptions({
      queryKey: moderationKeys.card(entityType, entityId),
      queryFn: () => moderationApi.getCard(entityType, entityId),
      enabled: entityId.trim().length > 0,
    }),

  /**
   * One request, seeded from the queue row the reviewer clicked. The fetch
   * on open re-reads its status (another admin may have decided it since
   * the page loaded); the row fills the list-only fields the single-request
   * read may omit (`entity_name`, `parent`).
   */
  request: (seed: ChangeRequest) =>
    queryOptions({
      queryKey: moderationKeys.request(seed.id),
      queryFn: async () => ({
        ...seed,
        ...(await moderationApi.getChangeRequest(seed.id)),
      }),
      initialData: seed,
      initialDataUpdatedAt: 0,
    }),
};
